<template>
  <div class="ai-preparation-container">
    <!-- 主内容区域 -->
    <div class="main-content">
      <!-- 顶部按钮组 -->
      <div class="button-group">
        <button v-for="button in mainButtons" :key="button.id" class="main-button"
          :class="{ active: activeMainButton === button.id }" @click="handleMainButtonClick(button.id)">
          {{ button.label }}
        </button>
      </div>

      <!-- 自媒体训练子按钮组 -->
      <div v-if="activeMainButton === 'selfMedia'" class="sub-button-group">
        <button v-for="subButton in selfMediaSubButtons" :key="subButton.id" class="sub-button"
          :class="{ active: activeSubButton === subButton.id }" @click="handleSubButtonClick(subButton.id)">
          {{ subButton.label }}
        </button>
      </div>

      <!-- 其他训练类型的添加按钮 -->
      <div v-if="activeMainButton && activeMainButton !== 'selfMedia'" class="sub-button-group">
        <button class="sub-button orange-button" @click="handleAddTraining">
          训练课文
        </button>
      </div>

      <!-- 内容区域 -->
      <div class="content-area">
        <!-- 数据表格区域 -->
        <div class="table-content">
          <!-- 顶部信息栏 -->
          <div class="info-bar">
            <div class="info-item">
              <span class="info-label">{{ getTrainingTypeLabel() }}开发次数：</span>
              <span class="info-value">{{ trainingStats.developCount }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">到期时间：</span>
              <span class="info-value">{{ trainingStats.expireTime }}</span>
            </div>
          </div>

          <!-- 数据表格 -->
          <el-table :data="tableData" v-loading="loading" class="data-table"
            :header-cell-style="{
              background: 'transparent',
              color: '#e0e6ff',
              borderBottom: '1px solid rgba(255, 112, 72, 0.3)'
            }">
            <el-table-column type="index" label="序号" width="60" />
            <el-table-column prop="taskName" label="任务名称" min-width="120" />
            <el-table-column prop="type" label="类型" width="100">
              <template #default="{ row }">
                <el-tag effect="dark" class="type-tag">
                  {{ row.type }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column prop="keywords" label="关键词" min-width="150">
              <template #default="{ row }">
                <div class="keywords-content">
                  {{ row.keywords }}
                </div>
              </template>
            </el-table-column>
            <el-table-column prop="trainingCount" label="训练课文数量" width="120">
              <template #default="{ row }">
                <span class="count-text">{{ row.trainingCount }}篇</span>
              </template>
            </el-table-column>
            <el-table-column prop="publishedCount" label="已发布数量" width="110">
              <template #default="{ row }">
                <span class="count-text">{{ row.publishedCount }}篇</span>
              </template>
            </el-table-column>
            <el-table-column prop="status" label="状态" width="100">
              <template #default="{ row }">
                <el-tag :type="getStatusType(row.status)" effect="dark">
                  {{ getStatusText(row.status) }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column prop="time" label="时间" width="140" />
            <el-table-column label="操作" fixed="right" width="180">
              <template #default="{ row }">
                <el-button size="small" @click="handleViewPublish(row)" class="action-btn">
                  查看发布
                </el-button>
                <el-button size="small" type="danger" @click="handleDelete(row)" class="action-btn">
                  删除
                </el-button>
              </template>
            </el-table-column>
          </el-table>

          <!-- 分页 -->
          <div class="pagination">
            <el-pagination v-model:current-page="pagination.page" v-model:page-size="pagination.pageSize"
              :page-sizes="[10, 20, 50, 100]" :total="pagination.total" 
              layout="total, sizes, prev, pager, next, jumper"
              @size-change="handleSizeChange" @current-change="handleCurrentChange" />
          </div>
        </div>
      </div>
    </div>

    <!-- 添加训练任务对话框 -->
    <el-dialog v-model="showAddDialog" :title="dialogTitle" width="500px" :close-on-click-modal="false"
      class="training-dialog">
      <el-form :model="formData" :rules="formRules" ref="formRef" label-width="100px">
        <el-form-item label="任务名称" prop="taskName">
          <el-input v-model="formData.taskName" placeholder="请输入任务名称" />
        </el-form-item>

        <el-form-item label="类型" prop="type">
          <el-select v-model="formData.type" placeholder="请选择类型" class="type-select">
            <el-option v-for="type in typeOptions" :key="type.value" :label="type.label" :value="type.value" />
          </el-select>
        </el-form-item>

        <el-form-item label="关键词" prop="keywords">
          <el-input v-model="formData.keywords" type="textarea" placeholder="请输入关键词，多个关键词用逗号分隔" 
            :rows="3" />
        </el-form-item>

        <el-form-item label="训练数量" prop="trainingCount">
          <el-input-number v-model="formData.trainingCount" :min="1" :max="100" />
          <span class="unit-text">篇</span>
        </el-form-item>

        <el-form-item label="平台类型" prop="platform" v-if="activeMainButton === 'selfMedia'">
          <el-select v-model="formData.platform" placeholder="请选择发布平台" class="platform-select">
            <el-option v-for="platform in platformOptions" :key="platform.value" 
              :label="platform.label" :value="platform.value" />
          </el-select>
        </el-form-item>
      </el-form>

      <template #footer>
        <el-button @click="showAddDialog = false">取消</el-button>
        <el-button type="primary" @click="handleSubmit" :loading="submitting">
          开始训练
        </el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ElMessage, ElMessageBox } from 'element-plus'

export default {
  name: 'AiPreparation',
  data() {
    return {
      // 响应式数据
      activeMainButton: 'selfMedia',
      activeSubButton: 'zhihu',
      loading: false,
      showAddDialog: false,
      submitting: false,
      formRef: null,

      // 主按钮配置
      mainButtons: [
        { id: 'selfMedia', label: '自媒体训练' },
        { id: 'thirdMedia', label: '第三方媒体训练' },
        { id: 'b2b', label: 'B2B训练' },
        { id: 'aiAgent', label: '智能体官网训练' }
      ],

      // 自媒体子按钮配置
      selfMediaSubButtons: [
        { id: 'zhihu', label: '知乎' },
        { id: 'xiaohongshu', label: '小红书' },
        { id: 'gongzhonghao', label: '公众号' },
        { id: 'weibo', label: '微博' },
        { id: 'toutiao', label: '头条' },
        { id: 'souhu', label: '搜狐' },
        { id: 'qiehao', label: '企鹅号' },
        { id: 'kuaishou', label: '快手' },
        { id: 'jianshu', label: '简书' },
        { id: 'douyin', label: '抖音' },
        { id: 'bilibili', label: 'B站' },
        { id: 'baijiahao', label: '百家号' },
        { id: 'aiqiyi', label: '爱奇艺' },
        { id: 'wangyi', label: '网易' }
      ],

      // 训练统计
      trainingStats: {
        developCount: 86,
        expireTime: '2026-07-21'
      },

      // 分页数据
      pagination: {
        page: 1,
        pageSize: 10,
        total: 41
      },

      // 表格数据
      tableData: [
        {
          id: 1,
          taskName: '123214',
          type: '其他',
          keywords: '门窗框工厂张',
          trainingCount: 30,
          publishedCount: 20,
          status: 1,
          time: '9小时前'
        },
        {
          id: 2,
          taskName: '1',
          type: '其他',
          keywords: '劳务派遣',
          trainingCount: 20,
          publishedCount: 20,
          status: 1,
          time: '12小时前'
        },
        {
          id: 3,
          taskName: '11',
          type: '其他',
          keywords: '教练会投票制',
          trainingCount: 20,
          publishedCount: 0,
          status: 2,
          time: '13小时前'
        },
        {
          id: 4,
          taskName: '11-6',
          type: '其他',
          keywords: '云南服装法律咨询厅',
          trainingCount: 20,
          publishedCount: 20,
          status: 1,
          time: '1天前'
        },
        {
          id: 5,
          taskName: '1',
          type: '其他',
          keywords: '女性大餐',
          trainingCount: 20,
          publishedCount: 20,
          status: 1,
          time: '1天前'
        },
        {
          id: 1,
          taskName: '123214',
          type: '其他',
          keywords: '门窗框工厂张',
          trainingCount: 30,
          publishedCount: 20,
          status: 1,
          time: '9小时前'
        },
        {
          id: 2,
          taskName: '1',
          type: '其他',
          keywords: '劳务派遣',
          trainingCount: 20,
          publishedCount: 20,
          status: 1,
          time: '12小时前'
        },
        {
          id: 3,
          taskName: '11',
          type: '其他',
          keywords: '教练会投票制',
          trainingCount: 20,
          publishedCount: 0,
          status: 2,
          time: '13小时前'
        },
        {
          id: 4,
          taskName: '11-6',
          type: '其他',
          keywords: '云南服装法律咨询厅',
          trainingCount: 20,
          publishedCount: 20,
          status: 1,
          time: '1天前'
        },
        {
          id: 5,
          taskName: '1',
          type: '其他',
          keywords: '女性大餐',
          trainingCount: 20,
          publishedCount: 20,
          status: 1,
          time: '1天前'
        }
        
      ],

      // 表单数据
      formData: {
        taskName: '',
        type: '',
        keywords: '',
        trainingCount: 20,
        platform: ''
      },

      // 类型选项
      typeOptions: [
        { value: '其他', label: '其他' },
        { value: '概念', label: '概念' },
        { value: '儿童', label: '儿童' },
        { value: '电影', label: '电影' },
        { value: '最佳生活', label: '最佳生活' }
      ],

      // 平台选项
      platformOptions: [
        { value: 'zhihu', label: '知乎' },
        { value: 'xiaohongshu', label: '小红书' },
        { value: 'gongzhonghao', label: '公众号' },
        { value: 'weibo', label: '微博' },
        { value: 'toutiao', label: '头条' },
        { value: 'souhu', label: '搜狐' },
        { value: 'qiehao', label: '企鹅号' },
        { value: 'kuaishou', label: '快手' },
        { value: 'jianshu', label: '简书' },
        { value: 'douyin', label: '抖音' },
        { value: 'bilibili', label: 'B站' },
        { value: 'baijiahao', label: '百家号' },
        { value: 'aiqiyi', label: '爱奇艺' },
        { value: 'wangyi', label: '网易' }
      ],

      // 表单验证规则
      formRules: {
        taskName: [
          { required: true, message: '请输入任务名称', trigger: 'blur' }
        ],
        type: [
          { required: true, message: '请选择类型', trigger: 'change' }
        ],
        keywords: [
          { required: true, message: '请输入关键词', trigger: 'blur' }
        ],
        trainingCount: [
          { required: true, message: '请输入训练数量', trigger: 'blur' }
        ]
      }
    }
  },
  computed: {
    dialogTitle() {
      return `添加${this.getMainButtonLabel(this.activeMainButton)}任务`
    }
  },
  methods: {
    handleMainButtonClick(buttonId) {
      this.activeMainButton = buttonId
      if (buttonId !== 'selfMedia') {
        this.activeSubButton = null
      } else {
        this.activeSubButton = 'zhihu'
      }
      // 模拟加载数据
      this.loadTableData()
    },

    handleSubButtonClick(subButtonId) {
      this.activeSubButton = subButtonId
      // 模拟加载数据
      this.loadTableData()
    },

    handleAddTraining() {
      this.showAddDialog = true
      this.formData = {
        taskName: '',
        type: '',
        keywords: '',
        trainingCount: 20,
        platform: ''
      }
      // 清除表单验证状态
      this.$nextTick(() => {
        if (this.$refs.formRef) {
          this.$refs.formRef.clearValidate()
        }
      })
    },

    getMainButtonLabel(type) {
      const button = this.mainButtons.find(b => b.id === type)
      return button ? button.label : ''
    },

    getTrainingTypeLabel() {
      return this.getMainButtonLabel(this.activeMainButton)
    },

    getStatusType(status) {
      const statusMap = {
        1: 'success', // 已完成
        2: 'warning', // 训练中
        3: 'danger'   // 失败
      }
      return statusMap[status] || 'info'
    },

    getStatusText(status) {
      const statusMap = {
        1: '已完成',
        2: '训练中',
        3: '失败'
      }
      return statusMap[status] || '未知'
    },

    // 模拟加载表格数据
    async loadTableData() {
      this.loading = true
      try {
        // 模拟API调用延迟
        await new Promise(resolve => setTimeout(resolve, 500))
        // 在实际应用中这里会调用API获取数据
        console.log(`加载 ${this.activeMainButton} 数据`)
      } catch (error) {
        ElMessage.error('加载数据失败')
        console.error('加载数据失败:', error)
      } finally {
        this.loading = false
      }
    },

    handleSizeChange(size) {
      this.pagination.pageSize = size
      this.pagination.page = 1
      this.loadTableData()
    },

    handleCurrentChange(page) {
      this.pagination.page = page
      this.loadTableData()
    },

    async handleSubmit() {
      if (!this.$refs.formRef) return

      try {
        const valid = await this.$refs.formRef.validate()
        if (!valid) return

        this.submitting = true

        // 模拟API调用
        await new Promise(resolve => setTimeout(resolve, 1000))

        ElMessage.success('训练任务添加成功，开始训练中...')
        this.showAddDialog = false
        this.loadTableData()
      } catch (error) {
        ElMessage.error('添加失败')
        console.error('添加失败:', error)
      } finally {
        this.submitting = false
      }
    },

    handleViewPublish(row) {
      ElMessage.info(`查看 ${row.taskName} 的发布内容`)
    },

    async handleDelete(row) {
      try {
        await ElMessageBox.confirm(
          `确定要删除任务 "${row.taskName}" 吗？`,
          '提示',
          {
            confirmButtonText: '确定',
            cancelButtonText: '取消',
            type: 'warning'
          }
        )

        // 模拟删除操作
        await new Promise(resolve => setTimeout(resolve, 500))
        ElMessage.success('删除成功')
        this.loadTableData()
      } catch (error) {
        if (error !== 'cancel') {
          ElMessage.error('删除失败')
        }
      }
    }
  },
  mounted() {
    // 初始化加载数据
    this.loadTableData()
  }
}
</script>

<style scoped lang="scss">
.ai-preparation-container {
  padding: 24px;
  min-height: calc(100vh - 160px);
  background: transparent;
}

.main-content {
  background: hsla(0, 0%, 100%, .1);
  border-radius: 16px;
  padding: 24px;
  backdrop-filter: blur(10px);
}

.button-group {
  display: flex;
  gap: 16px;
  margin-bottom: 24px;
  flex-wrap: wrap;
}

.main-button {
  padding: 12px 24px;
  background: hsla(0, 0%, 100%, .1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  color: #e0e6ff;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: #ff7048;
    border-color: #ff7048;
    transform: translateY(-2px);
  }

  &.active {
    background: #ff7048;
    border-color: #ff7048;
  }
}

.sub-button-group {
  display: flex;
  gap: 12px;
  margin-bottom: 24px;
  align-items: center;
  flex-wrap: wrap;
  background: hsla(0, 0%, 100%, .1);
  border-radius: 8px;
  padding: 16px;
}

.sub-button {
  padding: 8px 16px;
  background: hsla(0, 0%, 100%, .1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 6px;
  color: #e0e6ff;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: #ff7048;
    border-color: #ff7048;
    color: #fff;
  }

  &.active {
    background: #ff7048;
    border-color: #ff7048;
    color: #fff;
  }

  &.orange-button {
    background: #ff7048;
    border-color: #ff7048;
    color: #fff;

    &:hover {
      background: #e55a3a;
      border-color: #e55a3a;
    }
  }
}

.content-area {
  min-height: 400px;
}

.table-content {
  background: hsla(0, 0%, 100%, .1);
  border-radius: 8px;
  padding: 20px;
}

.info-bar {
  display: flex;
  gap: 32px;
  margin-bottom: 20px;
  padding: 16px;
  background: rgba(255, 112, 72, 0.1);
  border-radius: 6px;
  border: 1px solid rgba(255, 112, 72, 0.2);

  .info-item {
    display: flex;
    align-items: center;
    gap: 8px;

    .info-label {
      color: #e0e6ff;
      font-size: 14px;
    }

    .info-value {
      color: #ff7048;
      font-weight: 600;
      font-size: 14px;
    }
  }
}

.data-table {
  background: transparent;

  :deep(.el-table) {
    background: transparent;

    th {
      background: transparent !important;
      color: #e0e6ff !important;
      border-bottom: 1px solid rgba(255, 112, 72, 0.3) !important;
    }

    tr {
      background: rgba(255, 255, 255, 0.05);

      &:hover {
        background: rgba(255, 255, 255, 0.1) !important;
      }
    }
  }

  .type-tag {
    background: rgba(255, 112, 72, 0.2);
    border: 1px solid rgba(255, 112, 72, 0.3);
    color: #ff7048;
  }

  .keywords-content {
    color: #e0e6ff;
    line-height: 1.4;
  }

  .count-text {
    color: #ff7048;
    font-weight: 500;
  }

  .action-btn {
    margin: 2px;
  }
}

.pagination {
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
  padding: 20px 0;
}

.unit-text {
  margin-left: 8px;
  color: #e0e6ff;
}

// 响应式设计
@media (max-width: 768px) {
  .ai-preparation-container {
    padding: 16px;
  }

  .button-group {
    gap: 12px;

    .main-button {
      padding: 10px 20px;
      font-size: 13px;
    }
  }

  .sub-button-group {
    gap: 8px;
    padding: 12px;

    .sub-button {
      padding: 6px 12px;
      font-size: 12px;
    }
  }

  .info-bar {
    flex-direction: column;
    gap: 12px;
  }
}

@media (max-width: 480px) {
  .main-content {
    padding: 16px;
  }

  .button-group {
    justify-content: center;
  }

  .sub-button-group {
    justify-content: center;
  }
}
</style>

<style>
/* 全局样式 - 修复对话框问题 */
.training-dialog .el-dialog {
  background: rgba(13, 5, 26, 0.95) !important;
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 112, 72, 0.3);
  border-radius: 16px;
}

.training-dialog .el-dialog__header {
  margin: 0 !important;
  padding: 20px !important;
}

.training-dialog .el-dialog__title {
  color: #ffffff !important;
  font-weight: 600;
  font-size: 18px;
}

.training-dialog .el-dialog__body {
  padding: 20px !important;
  background: transparent !important;
}

.training-dialog .el-dialog__footer {
  border-top: 1px solid rgba(255, 112, 72, 0.2) !important;
  padding: 16px 20px !important;
  margin: 0 !important;
}

/* 表单标签颜色 */
.training-dialog .el-form-item__label {
  color: #e0e6ff !important;
  font-weight: 500;
}

/* 输入框文字颜色 */
.training-dialog .el-input__inner,
.training-dialog .el-textarea__inner {
  color: #ffffff !important;
}

.training-dialog .el-input__inner::placeholder,
.training-dialog .el-textarea__inner::placeholder {
  color: rgba(255, 255, 255, 0.5) !important;
}

/* 输入框聚焦状态 */
.training-dialog .el-input__wrapper {
  background: rgba(255, 255, 255, 0.08) !important;
  border: 1px solid rgba(255, 112, 72, 0.3) !important;
  box-shadow: none !important;
}

.training-dialog .el-input__wrapper:hover {
  border-color: #ff7048 !important;
}

.training-dialog .el-input__wrapper.is-focus {
  border-color: #ff7048 !important;
  box-shadow: 0 0 0 1px #ff7048 !important;
}

/* 选择器样式 */
.training-dialog .el-select .el-input__inner {
  color: #ffffff !important;
}

.training-dialog .el-select .el-select__caret {
  color: #ff7048 !important;
}

/* 按钮样式 */
.training-dialog .el-button {
  border-radius: 8px;
  font-weight: 500;
  transition: all 0.3s ease;
}

.training-dialog .el-button--primary {
  background: #ff7048 !important;
  border-color: #ff7048 !important;
}

.training-dialog .el-button--primary:hover {
  background: #e55a3a !important;
  border-color: #e55a3a !important;
  transform: translateY(-1px);
}

/* 表格样式覆盖 */
.el-table {
  --el-table-bg-color: transparent !important;
  --el-table-tr-bg-color: transparent !important;
  --el-table-header-bg-color: transparent !important;
  --el-table-row-hover-bg-color: rgba(255, 255, 255, 0.05) !important;
  --el-table-current-row-bg-color: rgba(255, 112, 72, 0.1) !important;
  --el-table-header-text-color: #e0e6ff !important;
  --el-table-text-color: #e0e6ff !important;
  --el-table-border-color: rgba(255, 112, 72, 0.3) !important;
}

.el-table th,
.el-table tr,
.el-table td {
  background: transparent !important;
  color: #e0e6ff !important;
  border-bottom: 1px solid rgba(255, 112, 72, 0.1) !important;
}

.el-table th {
  border-bottom: 1px solid rgba(255, 112, 72, 0.3) !important;
}

.el-table .el-table__inner-wrapper::before {
  background-color: rgba(255, 112, 72, 0.3) !important;
}
</style>